import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Table, Modal, Form, Badge, Alert, Pagination } from "react-bootstrap";
import PermissionWrapper from "../components/PermissionWrapper";

import axiosInstance from "../services/BaseService";

interface Schedule {
  id: string;
  playTitle: string;
  playId: string;
  venue: string;
  date: string;
  time: string;
  duration: number;
  ticketPrice: number;
  availableSeats: number;
  totalSeats: number;
  status: string;
  organizerName?: string;
  organizerPhone?: string;
}

const ManagePlaySchedulesPage: React.FC = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [plays, setPlays] = useState<{id: string, title: string}[]>([]);

  const [selectedProgram, setSelectedProgram] = useState<string>("");
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [states, setStates] = useState<{stateCode: string, name: string}[]>([]);
  const [selectedState, setSelectedState] = useState<string>("");
  const [groups, setGroups] = useState<{_id: string, name: string}[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Modal form states
  const [modalCountry, setModalCountry] = useState<string>("");
  const [modalState, setModalState] = useState<string>("");
  const [modalGroup, setModalGroup] = useState<string>("");
  const [modalStates, setModalStates] = useState<{stateCode: string, name: string}[]>([]);
  const [modalGroups, setModalGroups] = useState<{_id: string, name: string}[]>([]);
  const [modalPrograms, setModalPrograms] = useState<{id: string, title: string}[]>([]);
  const [editGroupName, setEditGroupName] = useState<string>("");
  const [formData, setFormData] = useState({
    programId: "",
    startDate: "",
    endDate: "",
    timing: "",
    address: "",
    pinCode: "",
    state: "",
    country: "",
    organizerName: "",
    organizerPhone: "",
    upiId: "",
    upiName: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    accountHolderName: ""
  });

  useEffect(() => {
    if (selectedCountry) {
      fetchStates();
      setSelectedState("");
      setSelectedGroup("");
      setSelectedProgram("");
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedState) {
      fetchGroups();
      setSelectedGroup("");
      setSelectedProgram("");
    }
  }, [selectedState]);

  useEffect(() => {
    if (selectedGroup) {
      fetchPlays();
      setSelectedProgram("");
    }
  }, [selectedGroup]);

  useEffect(() => {
    if (selectedProgram) {
      fetchSchedules();
    } else {
      setSchedules([]);
    }
  }, [selectedProgram, currentPage]);

  // Modal form effects
  useEffect(() => {
    if (modalCountry) {
      fetchModalStates();
      setModalState("");
      setModalGroup("");
    }
  }, [modalCountry]);

  useEffect(() => {
    if (modalState) {
      fetchModalGroups();
      setModalGroup("");
    }
  }, [modalState]);

  useEffect(() => {
    if (modalGroup) {
      fetchModalPrograms();
    }
  }, [modalGroup]);

  const fetchSchedules = async () => {
    if (!selectedProgram) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/programschedules/program/${selectedProgram}`);
      const data = Array.isArray(response.data) ? response.data : [];
      const selectedProgramTitle = plays.find(p => p.id === selectedProgram)?.title || 'Unknown Program';
      const formattedSchedules = data.map((schedule: any) => ({
        id: schedule._id,
        playTitle: schedule.programId?.title || selectedProgramTitle,
        playId: schedule.programId?._id || schedule.programId || '',
        venue: schedule.address || 'No venue specified',
        date: schedule.startDate,
        time: schedule.timing,
        duration: 120,
        ticketPrice: 0,
        availableSeats: 100,
        totalSeats: 100,
        status: 'Active',
        organizerName: schedule.organizerName || '',
        organizerPhone: schedule.organizerPhone || ''
      }));
      setSchedules(formattedSchedules);
      setTotalPages(Math.ceil(formattedSchedules.length / 10));
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setSchedules([]);
    }
    setLoading(false);
  };

  const fetchStates = async () => {
    try {
      const response = await axiosInstance.get(`/states/country/${selectedCountry}`);
      setStates(response.data || []);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axiosInstance.get(`/groups?state=${selectedState}`);
      const groups = response.data.groups || response.data;
      setGroups(Array.isArray(groups) ? groups : []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    }
  };

  const fetchPlays = async () => {
    try {
      const response = await axiosInstance.get(`/programs/group/${selectedGroup}`);
      const programs = response.data.programs || response.data;
      const formattedPlays = programs.map((program: any) => ({
        id: program._id,
        title: program.title
      }));
      setPlays(formattedPlays);
    } catch (error) {
      console.error('Error fetching plays:', error);
    }
  };

  const fetchModalStates = async () => {
    try {
      const response = await axiosInstance.get(`/states/country/${modalCountry}`);
      setModalStates(response.data || []);
    } catch (error) {
      console.error('Error fetching modal states:', error);
    }
  };

  const fetchModalGroups = async () => {
    try {
      const response = await axiosInstance.get(`/groups?state=${modalState}`);
      const groups = response.data.groups || response.data;
      setModalGroups(Array.isArray(groups) ? groups : []);
    } catch (error) {
      console.error('Error fetching modal groups:', error);
    }
  };

  const fetchModalPrograms = async () => {
    try {
      const response = await axiosInstance.get(`/programs/group/${modalGroup}`);
      const programs = response.data.programs || response.data;
      const formattedPrograms = programs.map((program: any) => ({
        id: program._id,
        title: program.title
      }));
      setModalPrograms(formattedPrograms);
    } catch (error) {
      console.error('Error fetching modal programs:', error);
    }
  };

  const handleEdit = async (schedule: Schedule) => {
    // Fetch full schedule data with populated program
    try {
      const response = await axiosInstance.get(`/programschedules/${schedule.id}`);
      const fullSchedule = response.data;
      
      // Update schedule with program title
      const selectedProgramTitle = plays.find(p => p.id === selectedProgram)?.title || 'Unknown Program';
      const updatedSchedule = {
        ...schedule,
        playTitle: fullSchedule.programId?.title || selectedProgramTitle
      };
      
      setSelectedSchedule(updatedSchedule);
      
      // Set form data with existing values
      setFormData({
        programId: fullSchedule.programId?._id || fullSchedule.programId || "",
        startDate: fullSchedule.startDate ? new Date(fullSchedule.startDate).toISOString().split('T')[0] : "",
        endDate: fullSchedule.endDate ? new Date(fullSchedule.endDate).toISOString().split('T')[0] : "",
        timing: fullSchedule.timing || "",
        address: fullSchedule.address || "",
        pinCode: fullSchedule.pinCode || "",
        state: fullSchedule.state || "",
        country: fullSchedule.country || "",
        organizerName: fullSchedule.organizerName || "",
        organizerPhone: fullSchedule.organizerPhone || "",
        upiId: fullSchedule.upiId || "",
        upiName: fullSchedule.upiName || "",
        bankName: fullSchedule.bankName || "",
        accountNumber: fullSchedule.accountNumber || "",
        ifscCode: fullSchedule.ifscCode || "",
        accountHolderName: fullSchedule.accountHolderName || ""
      });
      
      // Set modal filters and fetch related data sequentially
      setModalCountry(fullSchedule.country || "");
      
      let groupId = "";
      let groupName = "";
      
      // Get group from program using the new API endpoint
      if (fullSchedule.programId?._id || fullSchedule.programId) {
        try {
          const groupResponse = await axiosInstance.get(`/programs/${fullSchedule.programId?._id || fullSchedule.programId}/group`);
          const groupData = groupResponse.data;
          groupId = groupData.groupId;
          groupName = groupData.groupName;
        } catch (error) {
          console.error('Error fetching group information:', error);
        }
      }
      
      // Set the group name for display
      setEditGroupName(groupName);
      
      // Fetch states for the country
      if (fullSchedule.country) {
        const statesResponse = await axiosInstance.get(`/states/country/${fullSchedule.country}`);
        setModalStates(statesResponse.data || []);
        setModalState(fullSchedule.state || "");
        
        // Set the group ID
        if (groupId) {
          setModalGroup(groupId);
        }
      }
      
    } catch (error) {
      console.error('Error fetching schedule details:', error);
      setSelectedSchedule(schedule);
    }
    
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this schedule?")) {
      try {
        await axiosInstance.delete(`/programschedules/${id}`);
        setSchedules(schedules.filter(schedule => schedule.id !== id));
      } catch (error: any) {
        if (error.response?.status === 400) {
          alert(error.response.data.error || "Cannot delete schedule with tickets. Please remove all tickets first.");
        } else {
          alert('Error deleting schedule. Please try again.');
        }
        console.error('Error deleting schedule:', error);
      }
    }
  };

  const handleAddNew = () => {
    setSelectedSchedule(null);
    setModalCountry("");
    setModalState("");
    setModalGroup("");
    setEditGroupName("");
    setFormData({
      programId: "",
      startDate: "",
      endDate: "",
      timing: "",
      address: "",
      pinCode: "",
      state: "",
      country: "",
      organizerName: "",
      organizerPhone: "",
      upiId: "",
      upiName: "",
      bankName: "",
      accountNumber: "",
      ifscCode: "",
      accountHolderName: ""
    });
    setShowModal(true);
  };

  const handleSaveSchedule = async () => {
    try {
      const scheduleData = {
        ...formData,
        state: modalState,
        country: modalCountry
      };
      
      if (selectedSchedule) {
        await axiosInstance.put(`/programschedules/${selectedSchedule.id}`, scheduleData);
      } else {
        await axiosInstance.post('/programschedules', scheduleData);
      }
      
      fetchSchedules();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving schedule:', error);
    }
  };



  const getStatusBadge = (status: string, availableSeats: number) => {
    if (status === "Sold Out" || availableSeats === 0) {
      return <Badge bg="danger">Sold Out</Badge>;
    } else if (status === "Active") {
      return <Badge bg="success">Active</Badge>;
    } else if (status === "Cancelled") {
      return <Badge bg="secondary">Cancelled</Badge>;
    }
    return <Badge bg="warning">Pending</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getOccupancyColor = (available: number, total: number) => {
    const percentage = ((total - available) / total) * 100;
    if (percentage >= 90) return "success";
    if (percentage >= 70) return "warning";
    return "info";
  };

  return (
    <PermissionWrapper pageName="ManagePlaySchedulesPage">
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
              <h2 className="mb-3 mb-md-0">
                <i className="bi bi-calendar2-week me-2"></i>
                Manage Play Schedules
              </h2>

            </div>
          </Col>
        </Row>

        {/* Filters */}
        <Row className="mb-4">
          <Col md={2}>
            <Form.Group>
              <Form.Label>Country</Form.Label>
              <Form.Select value={selectedCountry} onChange={(e) => setSelectedCountry(e.target.value)}>
                <option value="">Choose Country...</option>
                <option value="IN">India</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>State</Form.Label>
              <Form.Select value={selectedState} onChange={(e) => setSelectedState(e.target.value)} disabled={!selectedCountry}>
                <option value="">Choose State...</option>
                {states.map(state => (
                  <option key={state.stateCode} value={state.stateCode}>{state.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Group</Form.Label>
              <Form.Select value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)} disabled={!selectedState}>
                <option value="">Choose Group...</option>
                {groups.map(group => (
                  <option key={group._id} value={group._id}>{group.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Program/Play</Form.Label>
              <Form.Select value={selectedProgram} onChange={(e) => setSelectedProgram(e.target.value)} disabled={!selectedGroup}>
                <option value="">Choose Program...</option>
                {plays.map(play => (
                  <option key={play.id} value={play.id}>{play.title}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <Button variant="primary" onClick={handleAddNew}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Schedule
            </Button>
          </Col>
        </Row>

        {!selectedProgram && (
          <Row className="mb-3">
            <Col>
              <Alert variant="info" className="text-center">
                <i className="bi bi-info-circle me-2"></i>
                Please select Country → State → Group → Program to view schedules.
              </Alert>
            </Col>
          </Row>
        )}

        {selectedProgram && (
          <Row>
            <Col>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Schedules for {plays.find(p => p.id === selectedProgram)?.title}</h5>
                  {loading && <div className="spinner-border spinner-border-sm" />}
                </Card.Header>
                <Card.Body className="p-0">
                  {schedules.length > 0 ? (
                    <>
                      <div className="p-3">
                        <Row className="g-3">
                          {schedules.map((schedule) => (
                            <Col xs={12} sm={6} lg={4} key={schedule.id}>
                              <Card className="h-100 shadow-sm">
                                <Card.Body>
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <h6 className="text-primary mb-0">{schedule.playTitle}</h6>
                                    <div className="d-flex gap-1">
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => handleEdit(schedule)}
                                        title="Edit Schedule"
                                      >
                                        <i className="bi bi-pencil"></i>
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleDelete(schedule.id)}
                                        title="Delete Schedule"
                                      >
                                        <i className="bi bi-trash"></i>
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="mb-2">
                                    <i className="bi bi-calendar me-2 text-muted"></i>
                                    <strong>{formatDate(schedule.date)}</strong>
                                  </div>
                                  <div className="mb-2">
                                    <i className="bi bi-clock me-2 text-muted"></i>
                                    {schedule.time}
                                  </div>
                                  <div className="mb-0">
                                    <i className="bi bi-geo-alt me-2 text-muted"></i>
                                    <small className="text-muted">{schedule.venue}</small>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </div>
                      {totalPages > 1 && (
                        <div className="d-flex justify-content-center p-3">
                          <Pagination>
                            <Pagination.Prev 
                              disabled={currentPage === 1}
                              onClick={() => setCurrentPage(currentPage - 1)}
                            />
                            {[...Array(totalPages)].map((_, i) => (
                              <Pagination.Item
                                key={i + 1}
                                active={i + 1 === currentPage}
                                onClick={() => setCurrentPage(i + 1)}
                              >
                                {i + 1}
                              </Pagination.Item>
                            ))}
                            <Pagination.Next 
                              disabled={currentPage === totalPages}
                              onClick={() => setCurrentPage(currentPage + 1)}
                            />
                          </Pagination>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center p-4">
                      <i className="bi bi-calendar-x display-1 text-muted"></i>
                      <h5 className="mt-3 text-muted">No schedules found</h5>
                      <p className="text-muted">Create a new schedule for this program.</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Schedule Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedSchedule ? "Edit Schedule" : "Add New Schedule"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              {/* Program Selection with Filters */}
              {selectedSchedule ? (
                <>
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Country</Form.Label>
                        <Form.Control
                          type="text"
                          value={modalCountry === 'IN' ? 'India' : modalCountry}
                          disabled
                          className="bg-light"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>State</Form.Label>
                        <Form.Control
                          type="text"
                          value={modalStates.find(s => s.stateCode === modalState)?.name || modalState}
                          disabled
                          className="bg-light"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Group</Form.Label>
                        <Form.Control
                          type="text"
                          value={editGroupName || ''}
                          disabled
                          className="bg-light"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Program</Form.Label>
                        <Form.Control
                          type="text"
                          value={selectedSchedule.playTitle}
                          disabled
                          className="bg-light"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              ) : (
                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Country</Form.Label>
                      <Form.Select value={modalCountry} onChange={(e) => setModalCountry(e.target.value)}>
                        <option value="">Choose Country...</option>
                        <option value="IN">India</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>State</Form.Label>
                      <Form.Select value={modalState} onChange={(e) => setModalState(e.target.value)} disabled={!modalCountry}>
                        <option value="">Choose State...</option>
                        {modalStates.map(state => (
                          <option key={state.stateCode} value={state.stateCode}>{state.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Group</Form.Label>
                      <Form.Select value={modalGroup} onChange={(e) => setModalGroup(e.target.value)} disabled={!modalState}>
                        <option value="">Choose Group...</option>
                        {modalGroups.map(group => (
                          <option key={group._id} value={group._id}>{group.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Program</Form.Label>
                      <Form.Select value={formData.programId} onChange={(e) => setFormData({...formData, programId: e.target.value})} disabled={!modalGroup}>
                        <option value="">Choose Program...</option>
                        {modalPrograms.map(program => (
                          <option key={program.id} value={program.id}>{program.title}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              )}
              
              {/* Schedule Details */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Timing</Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.timing}
                      onChange={(e) => setFormData({...formData, timing: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Pin Code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter pin code"
                      value={formData.pinCode}
                      onChange={(e) => setFormData({...formData, pinCode: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Enter venue address"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Organizer Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter organizer name"
                      value={formData.organizerName}
                      onChange={(e) => setFormData({...formData, organizerName: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Organizer Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="Enter organizer phone"
                      value={formData.organizerPhone}
                      onChange={(e) => setFormData({...formData, organizerPhone: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Payment Details */}
              <hr />
              <h6 className="mb-3">Payment Details</h6>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>UPI ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter UPI ID (e.g., user@paytm)"
                      value={formData.upiId}
                      onChange={(e) => setFormData({...formData, upiId: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>UPI Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter UPI account holder name"
                      value={formData.upiName}
                      onChange={(e) => setFormData({...formData, upiName: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Bank Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter bank name"
                      value={formData.bankName}
                      onChange={(e) => setFormData({...formData, bankName: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Account Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter account number"
                      value={formData.accountNumber}
                      onChange={(e) => setFormData({...formData, accountNumber: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>IFSC Code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter IFSC code"
                      value={formData.ifscCode}
                      onChange={(e) => setFormData({...formData, ifscCode: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Account Holder Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter account holder name"
                      value={formData.accountHolderName}
                      onChange={(e) => setFormData({...formData, accountHolderName: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveSchedule}>
              {selectedSchedule ? "Update" : "Create"} Schedule
            </Button>
          </Modal.Footer>
        </Modal>


      </Container>
    </PermissionWrapper>
  );
};

export default ManagePlaySchedulesPage;